import Icon from "@/components/Icon";
import { IAlbumImageInfo } from "@/modules/album/store";
import { loginStore } from "@/modules/login/store";
import { WithClassName } from "@/types/base";
import { genBlock } from "@/utils/bem";
import { Editor_Gallery_Words, useTranslate } from "@/utils/lang";
import { Box, Button, Image, SystemStyleObject, useBoolean } from "@chakra-ui/react";
import classNames from "classnames";
import React, { useMemo } from "react";
import { RenderImageProps } from "react-photo-gallery";
import { ReactComponent as CheckboxSvg } from "@/assets/icons/checkbox.svg";
import "./style.scss";
import { Actions } from "@/utils/api";
import { toast } from "@/components/Toast";
import { langStore } from "@noli/business";
import { DuplicateMeta, getLatestValidMeta, getWHFromMeta } from "@/types/meta";
import { compressedUrl } from "@/utils/string";
// import { DuplicateMeta } from "@/types/meta";

export type FormatImageDataType = RenderImageProps<{
  imageData: IAlbumImageInfo;
  src: string;
  width: number;
  height: number;
  key: string;
}>;
interface AlbumImageItemProps {
  data: FormatImageDataType;
  onShowDetail?: (image: IAlbumImageInfo) => void;
  onSelect?: (imageId: number) => void;
  selectable?: boolean;
  selected?: boolean;
}

const block = genBlock("c-creator-album-image-item");
const AlbumImageItem: React.FC<WithClassName<AlbumImageItemProps>> = ({
  className,
  data,
  onShowDetail,
  onSelect,
  selectable,
  selected,
}) => {
  const { translate } = useTranslate();
  const [adding, addingOp] = useBoolean(false);
  const hoverStyle = useMemo<SystemStyleObject | undefined>(() => {
    if (selectable) return undefined;

    return {
      "&:hover": {
        [`.${block("actions")}`]: {
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-end",
        },
      },
    };
  }, [selectable]);

  async function handleAddToBoard(e: React.MouseEvent<any, MouseEvent>) {
    e.stopPropagation();
    addingOp.on();
    try {
      const fromMetaJson = JSON.parse(data.photo.imageData.meta_json);
      const validMeta = getLatestValidMeta(fromMetaJson);

      if (!validMeta) throw Error();
      console.log("validMeta: ", validMeta);
      console.log("getWHFromMeta(validMeta): ", getWHFromMeta(validMeta));
      const metaJson: DuplicateMeta = {
        type: "duplicate",
        data: {
          url: data.photo.src,
          wh: getWHFromMeta(validMeta),
          from: fromMetaJson,
          duplicateFrom: "album",
        },
      };
      await Actions.importMeta({
        t: toast,
        lang: langStore.tgt,
        type: "duplicate",
        meta: metaJson,
        setupInfo: { checkBusy: false, busyAliases: [] },
        opt: null,
      });
      addingOp.off();
    } catch (err) {
      addingOp.off();
      console.error("err: ", err);
    }
  }

  function handleCheckDetail(e: React.MouseEvent<any, MouseEvent>) {
    e.stopPropagation();
    if (loginStore.isGuest) return;
    onShowDetail?.(data.photo.imageData);
  }

  function handleSelect(e: React.MouseEvent<any, MouseEvent>) {
    e.stopPropagation();
    if (selectable) {
      onSelect?.(data.photo.imageData.id);
    }
  }
  return (
    <Box
      className={classNames(className, block())}
      w={data?.photo?.width}
      h={data?.photo?.height}
      left={data.left}
      key={data?.photo?.key}
      top={data.top}
      sx={hoverStyle}
      onClick={handleSelect}
    >
      <Image
        src={compressedUrl(data.photo.src, 320)}
        w={data?.photo?.width}
        h={data?.photo?.height}
        className={block("preview")}
      />
      {selectable ? (
        <Box pos="absolute" top="4px" left="4px">
          {selected ? (
            <Icon svg={CheckboxSvg} />
          ) : (
            <Box
              w="16px"
              h="16px"
              border="1px solid"
              borderColor="text.secondary"
              bg="bg.panelBg"
              rounded="4px"
              _hover={{ borderColor: "brand.primary" }}
            />
          )}
        </Box>
      ) : null}
      <Box
        className={block("actions")}
        w={data?.photo?.width}
        h={data?.photo?.height}
        onClick={handleCheckDetail}
      >
        {loginStore.isGuest ? null : (
          <Button
            variant="outline"
            color="brand.primary"
            borderColor="brand.primary"
            mb="6px"
            size="sm"
            rounded="full"
            w="94px"
            h="24px"
            px="1px"
          >
            {translate(Editor_Gallery_Words["check-detail"])}
          </Button>
        )}
        <Button
          variant="outline"
          color="brand.primary"
          borderColor="brand.primary"
          size="sm"
          rounded="full"
          w="94px"
          h="24px"
          px="1px"
          isLoading={adding}
          loadingText={translate(Editor_Gallery_Words["add-to-graph"])}
          onClick={handleAddToBoard}
        >
          {translate(Editor_Gallery_Words["add-to-graph"])}
        </Button>
      </Box>
    </Box>
  );
};

export const CreatorAlbumImageItem = AlbumImageItem;
